/**
 * Menu Extraction Review Component
 * Two-column interface for reviewing AI-extracted menu data
 */

import { DropdownSelect } from '@components/common/DropdownSelect';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { useDepartments } from '@hooks/menu';
import { selectEffectiveOutletId } from '@store/outlets';
import {
  commitExtraction,
  declineAndRerunExtraction,
  saveExtractionEdits,
  selectCurrentExtraction,
  selectExtractionStats,
  selectIsCommitting,
  selectIsExtracting,
} from '@store/menuExtractions';
import { AlertCircle, Check, Loader2, RefreshCw } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import documentsAnimation from '../../../assets/lottie/documents_scan.json';
import { ExtractedItem, MenuExtractionOutput } from '../../../types/menu';
import { apiError, apiSuccess } from '../../../util/notify';
import { LottieAnimation } from '../../common/LottieAnimation';
import { CategorySection, ExtractionStats } from './components';

interface MenuExtractionReviewProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

/**
 * Department Mapping Row Component
 */
interface DepartmentMappingRowProps {
  name: string;
  mapping: string;
  departments: { id: string; name: string }[];
  onMap: (departmentId: string) => void;
}

const DepartmentMappingRow: React.FC<DepartmentMappingRowProps> = ({
  name,
  mapping,
  departments,
  onMap,
}) => (
  <div className="flex items-center gap-3">
    <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
      <span className="text-sm font-medium text-gray-700">{name}</span>
    </div>
    <span className="text-gray-400">→</span>
    <DropdownSelect
      value={mapping}
      onChange={onMap}
      options={[
        { value: '', label: 'Select department…' },
        ...departments.map((dept) => ({ value: dept.id, label: dept.name })),
      ]}
      searchable={departments.length > 10}
    />
  </div>
);

/**
 * Addon Badge Component
 */
const AddonBadge: React.FC<{ name: string }> = ({ name }) => (
  <span className="px-2 py-1 bg-purple-50 text-purple-700 text-sm rounded-lg">
    {name}
  </span>
);

export const MenuExtractionReview: React.FC<MenuExtractionReviewProps> = ({
  onComplete,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectEffectiveOutletId);
  const { data: allDepartments = [] } = useDepartments(outletId);
  const departments = allDepartments.filter((d) => d.status === 'active');
  const extraction = useAppSelector(selectCurrentExtraction);
  const stats = useAppSelector(selectExtractionStats);
  const isExtracting = useAppSelector(selectIsExtracting);
  const isCommitting = useAppSelector(selectIsCommitting);

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [editingItem, setEditingItem] = useState<{
    categoryIndex: number;
    itemIndex: number;
  } | null>(null);
  const [departmentMappings, setDepartmentMappings] = useState<
    Record<string, string>
  >({});

  // Initialize department mappings from extraction output
  const output = extraction?.json_output;

  // Build a set of unique department names from extracted data
  const extractedDepartmentNames = useMemo(() => {
    if (!output) return new Set<string>();
    const names = new Set<string>();
    output.categories.forEach((cat) => {
      cat.items.forEach((item) => {
        if (item.department) {
          names.add(item.department);
        }
      });
    });
    return names;
  }, [output]);

  const toggleCategory = useCallback((categoryName: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  }, []);

  const handleDepartmentMapping = useCallback(
    (extractedName: string, departmentId: string) => {
      setDepartmentMappings((prev) => ({
        ...prev,
        [extractedName]: departmentId,
      }));
    },
    [],
  );

  const handleCommit = useCallback(async () => {
    if (!extraction?.id || !outletId) return;

    // Validate all departments are mapped
    const unmappedDepartments = Array.from(extractedDepartmentNames).filter(
      (name) => !departmentMappings[name],
    );

    if (unmappedDepartments.length > 0) {
      apiError(`Please map all departments: ${unmappedDepartments.join(', ')}`);
      return;
    }

    try {
      await dispatch(
        commitExtraction({
          extraction_id: extraction.id,
          outlet_id: outletId,
          department_mappings: departmentMappings,
        }),
      ).unwrap();

      apiSuccess('Menu items have been added successfully!');
      onComplete?.();
    } catch (error) {
      apiError(
        error instanceof Error ? error.message : 'Failed to commit menu',
      );
    }
  }, [
    extraction?.id,
    outletId,
    extractedDepartmentNames,
    departmentMappings,
    dispatch,
    onComplete,
  ]);

  const handleDeclineAndRerun = useCallback(async () => {
    if (!extraction?.id || !outletId) return;

    try {
      await dispatch(
        declineAndRerunExtraction({
          extractionId: extraction.id,
          outletId,
        }),
      ).unwrap();

      apiSuccess('Extraction declined. A new version will be processed.');
    } catch (error) {
      apiError(
        error instanceof Error ? error.message : 'Failed to rerun extraction',
      );
    }
  }, [extraction?.id, outletId, dispatch]);

  const handleSaveItemEdit = useCallback(
    async (
      categoryIndex: number,
      itemIndex: number,
      updatedItem: ExtractedItem,
    ) => {
      if (!extraction?.id || !output) return;

      const updatedCategories = [...output.categories];
      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        items: updatedCategories[categoryIndex].items.map((item, idx) =>
          idx === itemIndex ? updatedItem : item,
        ),
      };

      const updatedOutput: MenuExtractionOutput = {
        ...output,
        categories: updatedCategories,
      };

      try {
        await dispatch(
          saveExtractionEdits({
            extractionId: extraction.id,
            output: updatedOutput,
          }),
        ).unwrap();
        setEditingItem(null);
      } catch {
        apiError('Failed to save changes');
      }
    },
    [extraction?.id, output, dispatch],
  );

  // Empty state - no extraction
  if (!extraction || !output) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <AlertCircle size={48} className="mx-auto mb-4 text-gray-400" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No Extraction Available
        </h3>
        <p className="text-gray-500">
          Upload a menu file to start the extraction process.
        </p>
      </div>
    );
  }

  // Processing state
  if (isExtracting) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="w-32 h-32 mx-auto mb-4">
          <LottieAnimation animationData={documentsAnimation} />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Processing...
        </h3>
        <p className="text-gray-500">Please wait while we process your menu.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Header */}
      <ExtractionStats
        categories={stats.categories}
        items={stats.items}
        addons={stats.addons}
      />

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Extracted Categories & Items */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <span className="font-medium text-gray-900">
              Extracted Categories & Items
            </span>
          </div>
          <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100">
            {output.categories.map((category, catIndex) => (
              <CategorySection
                key={category.category_name}
                category={category}
                categoryIndex={catIndex}
                isExpanded={expandedCategories.has(category.category_name)}
                onToggle={() => toggleCategory(category.category_name)}
                editingItem={editingItem}
                onEditItem={(itemIndex) =>
                  setEditingItem({ categoryIndex: catIndex, itemIndex })
                }
                onSaveItem={(itemIndex, item) =>
                  handleSaveItemEdit(catIndex, itemIndex, item)
                }
                onCancelEdit={() => setEditingItem(null)}
              />
            ))}
          </div>
        </div>

        {/* Right Column - Department Mappings */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-medium text-gray-900">Department Mappings</h3>
              <p className="text-sm text-gray-500 mt-1">
                Map extracted departments to your outlet&apos;s departments
              </p>
            </div>
            <div className="p-4 space-y-3">
              {Array.from(extractedDepartmentNames).map((name) => (
                <DepartmentMappingRow
                  key={name}
                  name={name}
                  mapping={departmentMappings[name] || ''}
                  departments={departments}
                  onMap={(deptId) => handleDepartmentMapping(name, deptId)}
                />
              ))}
              {extractedDepartmentNames.size === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No departments detected in extraction
                </p>
              )}
            </div>
          </div>

          {/* Discovered Addons */}
          {output.addon_library_discovery.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-medium text-gray-900">
                  Discovered Addons ({output.addon_library_discovery.length})
                </h3>
              </div>
              <div className="p-4">
                <div className="flex flex-wrap gap-2">
                  {output.addon_library_discovery.map((addon) => (
                    <AddonBadge key={addon} name={addon} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleDeclineAndRerun}
          disabled={isExtracting || isCommitting}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} />
          <span>Decline & Rerun</span>
        </button>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isCommitting}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCommit}
            disabled={isCommitting || isExtracting}
            className="flex items-center gap-2 px-6 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors disabled:opacity-50"
          >
            {isCommitting ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>Committing...</span>
              </>
            ) : (
              <>
                <Check size={18} />
                <span>Add to Menu</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
